import { Maximize2 } from "lucide-react";
import { CSSProperties, ReactNode } from "react";
import { flushSync } from "react-dom";
import { useNavigate } from "react-router";

import { StatusPill } from "nazare-ui/components";

import { ScrollShadow } from "../../../scroll-shadow";
import { SkeletonBlock } from "../../../skeleton-block";
import { useScrollEdges } from "../../../use-scroll-edges";
import { useSimulatedLoad } from "../../../use-simulated-load";
import styles from "./summary-cards.module.css";

type ActiveRun = {
  /** `wrun_<ULID>` — same id shape as the reference dashboard. */
  id: string;
  workflowName: string;
  elapsed: string;
};

type WorkflowSummary = {
  id: string;
  name: string;
  description: string;
  /** `false` for internal/chained workflows with no manual "Run" action
   * (e.g. "Send Notification") — mirrors the reference dashboard. */
  runnable: boolean;
};

type ScheduleStatus = "enabled" | "disabled";

type ScheduledWorkflow = {
  id: string;
  name: string;
  cron: string;
  status: ScheduleStatus;
  /** Hardcoded display string, not a `Date` — Workflow scripts forbid
   * non-deterministic calls like `new Date()`/`Date.now()`. */
  nextRun: string;
};

type InfraSchedule = {
  id: string;
  name: string;
  type: string;
  cron: string;
  status: ScheduleStatus;
  nextRun: string;
};

/** Static mock runs, longest-running first — there is no live
 * workflow-orchestration backend in `platform` yet. */
const ACTIVE_RUNS: ActiveRun[] = [
  { id: "wrun_01KWYK4C3NJMDM8H2CBG0S8N3R", workflowName: "ingestGmailInbox", elapsed: "13m 58s" },
  { id: "wrun_01KWYKA11QB4V3JRFCSP643N8B", workflowName: "ingestGmailInbox", elapsed: "10m 53s" },
  { id: "wrun_01KWYK2Z5G503MF8Q80WFDHSJJ", workflowName: "ingestGmailInbox", elapsed: "8m 44s" },
  { id: "wrun_01KWYKPRQKD8YA05V8XJGZD2BH", workflowName: "ingestGmailInbox", elapsed: "3m 55s" },
  { id: "wrun_01KWYKPCKVXGVF7V839VH1WTJB", workflowName: "ingestGmailInbox", elapsed: "4m 07s" },
  { id: "wrun_01KWYKPCDAT591FNP13YK0FCFJ", workflowName: "ingestGmailInbox", elapsed: "4m 08s" },
  { id: "wrun_01KWYKQ297MDNWG506NSZ2VZQ9", workflowName: "identifyCompanyUpdate", elapsed: "44s" },
  { id: "wrun_01KWYKPY61027WMHNA57M8PRKD", workflowName: "identifyCompanyUpdate", elapsed: "47s" },
];

/** Real workflow names/descriptions from the reference dashboard — static
 * mock content, not fetched. "Send Notification" is internal/chained and
 * has no manual "Run" action, same as the reference. */
const WORKFLOWS: WorkflowSummary[] = [
  {
    id: "weekly-sentiment-summary",
    name: "Weekly Sentiment Summary",
    description:
      "Monday digest of the previous week's sentiment forms with an AI-generated overview, emailed to the sentiment whitelist.",
    runnable: true,
  },
  {
    id: "sentiment-form-chaser",
    name: "Sentiment Form Chaser",
    description:
      "Per-member reminder of any sentiment forms assigned in the last week that are still outstanding.",
    runnable: true,
  },
  {
    id: "agenda-reminder",
    name: "Agenda Reminder",
    description:
      "Friday reminder to the sentiment recipients to submit agenda items for the next investment meeting.",
    runnable: true,
  },
  {
    id: "daily-report-alerts",
    name: "Daily Report Alerts",
    description:
      "Refresh every report with an active daily subscription and notify subscribers of new rows.",
    runnable: true,
  },
  {
    id: "weekly-report-alerts",
    name: "Weekly Report Alerts",
    description:
      "Refresh every report with an active weekly subscription and notify subscribers of new rows.",
    runnable: true,
  },
  {
    id: "send-notification",
    name: "Send Notification",
    description: "Dispatch a single notification job (all enabled channels) — internal, enqueued per job.",
    runnable: false,
  },
  {
    id: "identify-company-update",
    name: "Identify Company Update",
    description: "Classify one ingested email and, when positive, chain a metrics extraction run.",
    runnable: true,
  },
  {
    id: "extract-metrics",
    name: "Extract Metrics",
    description: "Extract company metrics from one email and persist the draft metrics.",
    runnable: true,
  },
  {
    id: "eval-metrics-extraction",
    name: "Eval Metrics Extraction",
    description: "Score the metrics extractor against the expected-results set and persist the run.",
    runnable: true,
  },
  {
    id: "ingest-gmail-inbox",
    name: "Ingest Gmail — Inbox",
    description: "Finite, self-rescheduling sync of one Workspace inbox onto S3 and the message index.",
    runnable: true,
  },
  {
    id: "ingest-gmail-manager",
    name: "Ingest Gmail — Manager",
    description: "Hourly reconciliation of the active Workspace inbox set. Singleton run.",
    runnable: true,
  },
];

const SCHEDULED_WORKFLOWS: ScheduledWorkflow[] = [
  {
    id: "scheduled-agenda-reminder",
    name: "Agenda Reminder",
    cron: "cron(10 7 ? * FRI *)",
    status: "enabled",
    nextRun: "10 Jul, 14:10",
  },
  {
    id: "scheduled-eval-metrics-extraction",
    name: "Eval Metrics Extraction",
    cron: "cron(30 22 * * ? *)",
    status: "disabled",
    nextRun: "in 6h 46 min",
  },
  {
    id: "scheduled-daily-report-alerts",
    name: "Daily Report Alerts",
    cron: "cron(0 8 * * ? *)",
    status: "enabled",
    nextRun: "in 16h 18 min",
  },
  {
    id: "scheduled-weekly-report-alerts",
    name: "Weekly Report Alerts",
    cron: "cron(0 15 ? * FRI *)",
    status: "enabled",
    nextRun: "10 Jul, 22:00",
  },
  {
    id: "scheduled-sentiment-form-chaser",
    name: "Sentiment Form Chaser",
    cron: "cron(0 7 ? * FRI *)",
    status: "enabled",
    nextRun: "10 Jul, 14:00",
  },
  {
    id: "scheduled-weekly-sentiment-summary",
    name: "Weekly Sentiment Summary",
    cron: "cron(0 8 ? * MON *)",
    status: "enabled",
    nextRun: "13 Jul, 08:00",
  },
];

const INFRA_SCHEDULES: InfraSchedule[] = [
  {
    id: "infra-basecamp-fund-perf",
    name: "basecamp_fund_perf_data_ingestion-portfolio_watchlist",
    type: "ECS task",
    cron: "cron(15 5 ? * FRI *)",
    status: "disabled",
    nextRun: "10 Jul, 12:15",
  },
  {
    id: "infra-ecb-fx-ingest",
    name: "ecb_fx_ingest-ingest",
    type: "ECS task",
    cron: "cron(0 2 * * ? *)",
    status: "enabled",
    nextRun: "in 10h 16 min",
  },
  {
    id: "infra-email-agenda-run",
    name: "email_agenda-run",
    type: "ECS task",
    cron: "cron(10 7 ? * FRI *)",
    status: "disabled",
    nextRun: "10 Jul, 14:10",
  },
  {
    id: "infra-email-weekly-run",
    name: "email_weekly-run",
    type: "ECS task",
    cron: "cron(0 7 ? * MON *)",
    status: "disabled",
    nextRun: "13 Jul, 14:00",
  },
  {
    id: "infra-fund-overview-ingestion",
    name: "fund_overview_ingestion-fund_overview_watchlist",
    type: "ECS task",
    cron: "cron(0 1 ? * ? *)",
    status: "disabled",
    nextRun: "in 9h 18 min",
  },
  {
    id: "infra-network-scores-refresh",
    name: "network_scores_refresh-batch",
    type: "ECS task",
    cron: "cron(0 4 * * ? *)",
    status: "enabled",
    nextRun: "in 13h 34 min",
  },
  {
    id: "infra-sentiment-digest-export",
    name: "sentiment_digest_export-batch",
    type: "ECS task",
    cron: "cron(45 6 ? * MON *)",
    status: "disabled",
    nextRun: "13 Jul, 07:45",
  },
  {
    id: "infra-knowledge-base-backup",
    name: "knowledge_base_backup-nightly",
    type: "ECS task",
    cron: "cron(0 3 * * ? *)",
    status: "enabled",
    nextRun: "in 4h 02 min",
  },
];

const SKELETON_ROW_COUNT = 5;

/**
 * Initial-mount placeholder for a card's row list — one `<li>` per real row,
 * shaped like the shared two-line `.row`/`.rowTop`/`.rowBottom` layout that
 * three of the four cards use for their real content (the "Workflows" card's
 * single-line-plus-button layout is close enough in height that reusing the
 * same shape here still reads naturally). Widths are varied per index rather
 * than uniform so the placeholder doesn't read as an obviously fake grid.
 */
function SkeletonSummaryRows() {
  return (
    <>
      {Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
        <li key={index} className={styles.row} aria-hidden="true">
          <div className={styles.rowTop}>
            <SkeletonBlock width={`${60 + ((index * 7) % 11)}%`} height={13} />
          </div>
          <div className={styles.rowBottom}>
            <SkeletonBlock width={`${36 + ((index * 5) % 9)}%`} height={11} />
          </div>
        </li>
      ))}
    </>
  );
}

type CardProps = {
  title: string;
  count: number;
  children: ReactNode;
  /** `view-transition-name` applied to this card's own root element — must
   * exactly match the name the destination full-page route applies to its
   * own root card, so the browser's native View Transitions API morphs one
   * into the other instead of cross-fading the whole page. */
  viewTransitionName: string;
  /** Full-page route this card's "maximise" button navigates to. */
  maximiseTo: string;
};

/** Shared shell for all four summary cards: header (title + count badge)
 * over a scrollable row list. */
function Card({ title, count, children, viewTransitionName, maximiseTo }: CardProps) {
  const navigate = useNavigate();
  const { ref: listRef, atTop, atBottom } = useScrollEdges<HTMLUListElement>();

  const goToFullPage = () => {
    if (!document.startViewTransition) {
      navigate(maximiseTo);
      return;
    }
    document.startViewTransition(() => flushSync(() => navigate(maximiseTo)));
  };

  return (
    <section
      className={styles.card}
      style={{ viewTransitionName } as unknown as CSSProperties}
    >
      <header className={styles.header}>
        <h2 className={styles.title}>{title}</h2>
        <span className={styles.count}>{count}</span>
        <button
          type="button"
          className={styles.maximiseButton}
          onClick={goToFullPage}
          aria-label={`Expand ${title}`}
          title={`Expand ${title}`}
        >
          <Maximize2 className={styles.maximiseIcon} aria-hidden="true" />
        </button>
      </header>
      <div className={styles.scrollShadowWrapper}>
        <ul ref={listRef} className={styles.list}>
          {children}
        </ul>
        <ScrollShadow atTop={atTop} atBottom={atBottom} />
      </div>
    </section>
  );
}

/**
 * Top row of the reference `/admin/workflows` dashboard: four summary
 * cards — Active runs, Workflows, Scheduled workflows, and Infra schedules
 * — laid out as a responsive 4-column grid. Below this belongs the
 * separate "Runs" table (a sibling component). All data here is static
 * mock content shaped after the real dashboard; there is no live
 * workflow-orchestration backend in `platform` yet.
 */
export function WorkflowSummaryCards() {
  const isLoading = useSimulatedLoad();

  return (
    <div className={styles.grid}>
      <Card
        title="Active runs"
        count={15}
        viewTransitionName="wf-card-active"
        maximiseTo="/workflows/active"
      >
        {isLoading ? (
          <SkeletonSummaryRows />
        ) : (
          ACTIVE_RUNS.map((run) => (
            <li key={run.id} className={styles.row}>
              <div className={styles.rowTop}>
                <StatusPill tone="info" className={styles.runningPill}>
                  <span className={styles.pulseDot} aria-hidden="true" />
                  Running
                </StatusPill>
                <span className={styles.rowTitle}>{run.workflowName}</span>
              </div>
              <div className={styles.rowBottom}>
                <span className={styles.rowMono} title={run.id}>
                  {run.id}
                </span>
                <span className={styles.rowMeta}>{run.elapsed}</span>
              </div>
            </li>
          ))
        )}
      </Card>

      <Card
        title="Workflows"
        count={11}
        viewTransitionName="wf-card-definitions"
        maximiseTo="/workflows/definitions"
      >
        {isLoading ? (
          <SkeletonSummaryRows />
        ) : (
          WORKFLOWS.map((workflow) => (
            <li key={workflow.id} className={`${styles.row} ${styles.workflowRow}`}>
              <span className={styles.workflowText}>
                <span className={styles.workflowName}>{workflow.name}</span>
                <span className={styles.workflowDescription} title={workflow.description}>
                  {workflow.description}
                </span>
              </span>
              {workflow.runnable && (
                <button type="button" className={styles.runButton}>
                  Run
                </button>
              )}
            </li>
          ))
        )}
      </Card>

      <Card
        title="Scheduled workflows"
        count={6}
        viewTransitionName="wf-card-scheduled"
        maximiseTo="/workflows/scheduled"
      >
        {isLoading ? (
          <SkeletonSummaryRows />
        ) : (
          SCHEDULED_WORKFLOWS.map((schedule) => (
            <li key={schedule.id} className={styles.row}>
              <div className={styles.rowTop}>
                <span className={styles.rowTitle}>{schedule.name}</span>
                <StatusPill tone={schedule.status === "enabled" ? "success" : "neutral"}>
                  {schedule.status === "enabled" ? "Enabled" : "Disabled"}
                </StatusPill>
              </div>
              <div className={styles.rowBottom}>
                <span className={styles.rowMono}>{schedule.cron}</span>
                <span className={styles.rowMeta}>{schedule.nextRun}</span>
              </div>
            </li>
          ))
        )}
      </Card>

      <Card
        title="Infra schedules"
        count={25}
        viewTransitionName="wf-card-infra"
        maximiseTo="/workflows/infra"
      >
        {isLoading ? (
          <SkeletonSummaryRows />
        ) : (
          INFRA_SCHEDULES.map((schedule) => (
            <li key={schedule.id} className={styles.row}>
              <div className={styles.rowTop}>
                <span className={styles.rowTitle} title={schedule.name}>
                  {schedule.name}
                </span>
                <StatusPill tone={schedule.status === "enabled" ? "success" : "neutral"}>
                  {schedule.status === "enabled" ? "Enabled" : "Disabled"}
                </StatusPill>
              </div>
              <div className={styles.rowBottom}>
                <span className={styles.infraType}>{schedule.type}</span>
                <span className={`${styles.rowMono} ${styles.infraCron}`}>{schedule.cron}</span>
                <span className={styles.rowMeta}>{schedule.nextRun}</span>
              </div>
            </li>
          ))
        )}
      </Card>
    </div>
  );
}
